import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { client } from "../apolloClient";
import { isMagicLinkUser, clearAuthState } from "../util/authCleanup";
import SessionExpiredModal from "../components/SessionExpiredModal";

// ─── Module-level event emitter (callable outside React) ───
// This allows apolloClient.ts onError link to trigger session expired
// without needing access to React context
const SESSION_EXPIRED_EVENT = "session-expired";

// Module-level flag set synchronously when session expiry is detected.
// Used by catch blocks to avoid showing alert() dialogs when the
// session-expired modal is about to appear (React state updates are
// async, so the context value may not have flushed yet).
let _sessionExpired = false;

export function emitSessionExpired(): void {
  _sessionExpired = true;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/**
 * Synchronous check for whether a session-expiry event has been emitted.
 * Use this in catch blocks to skip alert() when the session-expired modal
 * will handle the error instead.
 */
export function isSessionExpiredError(): boolean {
  return _sessionExpired;
}

// ─── Context definition ───
interface SessionExpiredContextType {
  isSessionExpired: boolean;
  triggerSessionExpired: () => void;
}

const SessionExpiredContext = createContext<
  SessionExpiredContextType | undefined
>(undefined);

interface SessionExpiredProviderProps {
  children: ReactNode;
}

export const SessionExpiredProvider: React.FC<SessionExpiredProviderProps> = ({
  children,
}) => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  // Capture auth type before clearing state so the modal shows the right button
  const [wasMagicLinkUser, setWasMagicLinkUser] = useState(false);

  // Ref-based guard to prevent duplicate triggers from concurrent failing
  // requests without relying on side effects inside a state updater.
  const hasTriggeredRef = useRef(false);

  const triggerSessionExpired = useCallback(() => {
    if (hasTriggeredRef.current) return; // Already expired, no-op
    hasTriggeredRef.current = true;
    console.warn("[SessionExpired] Session expiry detected");
    // Snapshot auth type before setting expired state, since clearing auth
    // state later would lose this information.
    setWasMagicLinkUser(isMagicLinkUser());
    setIsSessionExpired(true);
  }, []);

  // Listen for CustomEvent from non-React code (Apollo onError link)
  useEffect(() => {
    const handler = (): void => triggerSessionExpired();
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, [triggerSessionExpired]);

  // Clear all auth artifacts and redirect
  const clearAuthAndRedirect = useCallback(
    async (redirectPath: string): Promise<void> => {
      await clearAuthState(client);
      window.location.href = redirectPath;
    },
    [],
  );

  const handleRequestNewLink = useCallback((): void => {
    clearAuthAndRedirect("/auth/request");
  }, [clearAuthAndRedirect]);

  const handleReLogin = useCallback((): void => {
    clearAuthAndRedirect("/");
  }, [clearAuthAndRedirect]);

  const value: SessionExpiredContextType = {
    isSessionExpired,
    triggerSessionExpired,
  };

  return (
    <SessionExpiredContext.Provider value={value}>
      {children}
      {isSessionExpired && (
        <SessionExpiredModal
          wasMagicLinkUser={wasMagicLinkUser}
          onRequestNewLink={handleRequestNewLink}
          onReLogin={handleReLogin}
        />
      )}
    </SessionExpiredContext.Provider>
  );
};

export const useSessionExpired = (): SessionExpiredContextType => {
  const context = useContext(SessionExpiredContext);
  if (context === undefined) {
    throw new Error(
      "useSessionExpired must be used within a SessionExpiredProvider",
    );
  }
  return context;
};
